import React, { useState, useRef, useEffect } from 'react';
import { Button, TextareaAutosize, Typography, } from '@mui/material';
import { Tooltip } from 'react-tooltip';
import 'react-tooltip/dist/react-tooltip.css'; 
import CancelIcon from '@mui/icons-material/Cancel';
// import Fancybox from '../Profile/user-profile/Fancybox';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import DoneIcon from '@mui/icons-material/Done';
import SentimentSatisfiedOutlinedIcon from '@mui/icons-material/SentimentSatisfiedOutlined';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import EmojiPicker from 'emoji-picker-react';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ProfileCardRight from './ProfileCardRight';
import ArrowBackIosNewOutlinedIcon from '@mui/icons-material/ArrowBackIosNewOutlined';
import { toast } from 'react-toastify';
import { X, Search } from 'lucide-react';
import { formatMessageTimestamp } from '../../utils/dateTime';
import useSocket from '../../hooks/useSocket';
import { UploadFile } from '@mui/icons-material';
import { fileUpload } from '../../api/upload';
import PicRequest from './PicRequest'
import { getFileType } from '../../utils/fileUtils';
import ScheduleModal from './ScheduleModal';
import AlarmAddIcon from '@mui/icons-material/AlarmAdd';
import { getAvatarByGender, wrapEmojis } from '../../utils/CommonUtils';
import ClearIcon from '@mui/icons-material/Clear';

import { Fancybox } from "@fancyapps/ui";
import "@fancyapps/ui/dist/fancybox/fancybox.css";
import DisabledAccountOverlay from './DisabledAccountOverlay';

const MessageContent = ({ msg, senderId }) => {
  if (msg.messageType === "pic_request") {
    return <PicRequest msg={msg} userId={senderId} />;
  }

  const openGallery = (startIndex = 0) => {
    Fancybox.show(
      msg.files.map((file) => ({
        src: file.url,
        type: "image",
        caption: file.name,
      })),
      {
        startIndex,
        Carousel: { infinite: false },
        hideScrollbar: false,
        Thumbs: false,
      }
    );
  };

  return (
    <div
      className="message__html-content"
      style={{
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
        textAlign: "left",
      }}
    >
      {/* IMAGE MESSAGE */}
      {msg.messageType === "image" && msg.files?.length > 0 && (
        <div className='fancyPhotos-card'>
        <div
          className={`uploaded-images ${
            msg.files.length === 1 ? "single-image" : ""
          }`}
        >
          {msg.files.slice(0, 4).map((file, idx) => (
            <div
              className={`uploadImg ${idx === 3 && msg.files.length > 4 ? "overlay" : ""}`}
              key={idx}
              onClick={() => openGallery(idx)}
              style={{ cursor: "pointer" }}
            >
                <a className="overlay" >
              <img src={file.url} alt={file.name} />
              {idx === 3 && msg.files.length > 4 && (
                <div className="extra-count">
                  +{msg.files.length - 4} More
                </div>
              )}
                  </a>
            </div>
          ))}
        </div>
        </div>
      )}

      {/* AUDIO MESSAGE */}
      {msg.messageType === "audio" && msg.files?.length > 0 && (
        <div className="audio-message">
          {msg.files.map((file, idx) => (
            <audio
              key={idx}
              controls
              controlsList="nodownload"
              className="d-block mb-2"
            >
              <source src={file.url} type={file.fileType || "audio/mpeg"} />
            </audio>
          ))}
        </div>
      )}

      {/* TEXT MESSAGE */}
      {(msg.messageType === "text" || msg.message) && (
        <span className="message" 
         style={{ whiteSpace: "pre-wrap",wordBreak: "break-word" }}>{wrapEmojis(msg.message)}</span>
      )}
    </div>
  );
};

const ChatBox = ({
  chat,
  messages,
  messagesLoading,
  setMessages,
  senderId,
  receiverId,
  roomId,
  updateCounterAndLastSeen
}) => {

  const [openScheduleModal, setOpenScheduleModal] = useState(false);
  const handleScheduleSelect = (time) => {
    if (inputValue.trim() === '' && files.length === 0) {
      toast.error('Please enter a message or attach a file first');
      return;
    }
    handleSendMessage(time);
    setOpenScheduleModal(false);
  };



  const [isOpen, setIsOpen] = useState(false);
  // const [inputValue, setInputValue] = useState('');
  const [inputValue, setInputValue] = useState('');
  const textareaRef = useRef(null);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showProfileCard, setShowProfileCard] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);
  const { socket, data, sendMessage, notifyAdminConnection, handleTyping } = useSocket();
  const [files, setFiles] = useState([]);
  const [isRecording, setIsRecording] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);
  const mediaRecorderRef = useRef(null);
  const audioBlobRef = useRef(null);
  const audioStreamRef = useRef(null);
  const [audioFile, setAudioFile] = useState(null);
  const [fileUploading, setFileUploading] = useState(false);
  const [recordedAudio, setRecordedAudio] = useState(null);
  const [currentPlayTime, setCurrentPlayTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioDuration, setAudioDuration] = useState(0);
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  const audioRef = useRef(null);
  const playTimerRef = useRef(null);
  const emojiPickerRef = useRef(null);
  const inputRef = useRef(null);
  const timerRef = useRef(null);
  const isAudioFile = Boolean(files.find(file=>file.fileType === 'audio/wav'))

  // The room the SERVER resolved for (senderId, receiverId) via checkRoom. The `roomId`
  // prop comes from the inbox row, which can describe a different bot's conversation if
  // a debounced recentChatListResponse landed under the wrong selection -- sending with
  // it wrote the message into that other pair's room (ADMIN-18). Cleared on every
  // recipient change so a previous chat's room can never be reused.
  const resolvedRoomIdRef = useRef(null);
  useEffect(() => {
    resolvedRoomIdRef.current = null;
  }, [receiverId, senderId]);
  const effectiveRoomId = () => resolvedRoomIdRef.current || roomId;


  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const playRecordedAudio = () => {
    if (audioRef.current) {
      audioRef.current.play();
      setIsPlaying(true);

      playTimerRef.current = setInterval(() => {
        setCurrentPlayTime(audioRef.current?.currentTime || 0);
      }, 100);
    }
  };

  const pauseRecordedAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      setIsPlaying(false);
      clearInterval(playTimerRef.current);
    }
  };

  const handleAudioEnded = () => {
    setIsPlaying(false);
    setCurrentPlayTime(0);
    clearInterval(playTimerRef.current);
  };

    const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60)
      .toString()
      .padStart(2, '0');
    const secs = Math.floor(seconds % 60)
      .toString()
      .padStart(2, '0');
    return `${mins}:${secs}`;
  };

  const handleSendMessage = (scheduledDateTime = null) => {
    if (inputValue.trim() === '' && files.length === 0) return;
    const messageData = {
      senderId: senderId,
      receiverId: receiverId,
      roomId: effectiveRoomId(),
      message: inputValue,
      messageType: inputValue.trim() === '' ? getFileType(files[0]) : 'text',
      files: files,
      userAgentSent: null,
      scheduledDateTime: scheduledDateTime,
      schedule: scheduledDateTime ? true : false
    };

    sendMessage(messageData);
    setInputValue('');
    setFiles([]);
    setRecordedAudio(null)
  };

  // Autosize logic for the textarea
  const autosize = () => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = '0px'; // Reset height
      const height = Math.min(20 * 5, textarea.scrollHeight); // Max height: 5 lines
      textarea.style.height = `${height}px`; // Set new height
    }
  };

  useEffect(() => {
    autosize(); // Trigger on component load to adjust height if there's initial content
  }, [inputValue]);


  const handleSocketResponse = ({ roomId }) => {
    if(socket){
      const newRoomId = roomId?.toString();
      const previousRoomId = localStorage.getItem("chatRoomId");
      const previousSenderId = localStorage.getItem("chatSenderId");

      if (previousRoomId) {
        socket.emit("leaveRoom", { roomId: previousRoomId,userId:previousSenderId });
      }
      // This is the authoritative room for this pair; outgoing messages use it in
      // preference to the roomId prop.
      resolvedRoomIdRef.current = newRoomId || null;
      localStorage.setItem("chatRoomId", newRoomId);
      localStorage.setItem("chatSenderId", senderId);
    }
  };

  // `readAllMessagesResponse` means the MEMBER read what the bot sent him -- it says
  // nothing about the bot having read his. Flipping every message in the room turned
  // the member's own incoming messages blue too, so the ticks claimed a read that had
  // not happened (ADMIN-5). Only the bot's outgoing messages move, which is what the
  // web client and the app already do.
  const handleReadAllMessagesResponse =(data) =>{
      if(socket && data?.roomId===effectiveRoomId()){
        setMessages(preMsg=>preMsg?.map(msg=>{
          const msgSenderId = msg?.senderId?._id || msg?.senderId;
          if (String(msgSenderId) !== String(senderId)) return msg;
          return {
            ...msg,
            isDelivered: true,
            isRead: true
          }
        }))
    }
  }

  useEffect(() => {
    // notifyAdminConnection()
    if (socket) {
      // socket.on('messageResponse', updatedChat => {
      //   if (roomId === updatedChat?.result?.OneToOneId) {
      //     setMessages((prevMessages) => [
      //       ...prevMessages,
      //       updatedChat?.result
      //     ]);
      //   }
      //   else {
      //     updateCounterAndLastSeen(updatedChat?.result)
      //   }
      // });

      let typingTimeoutId = null;
      socket.on('typingResponse', (data) => {
        if (data?.response?.senderId === receiverId) {
          if (typingTimeoutId) {
            clearTimeout(typingTimeoutId);
          }
          setIsTyping(true);
          typingTimeoutId = setTimeout(() => {
            setIsTyping(false);
          }, 1000);
        }
      });
      socket.emit("checkRoom", { users: { participantId: receiverId, userId: senderId, isAdmin: true } });
      // Must be `on` + an explicit `off` in cleanup, not `once`. Switching chats
      // re-runs this effect while the previous checkRoom may still be in flight;
      // with `once` the first reply to arrive is consumed by whichever listeners
      // are registered, so the previous conversation's response could bind this
      // chat box to the wrong room and the correct reply would then be dropped.
      const onRoomResponse = (response) => {
        // Ignore a reply for a conversation we have already navigated away from.
        if (response?.participantId && response.participantId !== receiverId) return;
        handleSocketResponse(response);
      };
      socket.on("roomResponse", onRoomResponse);
      socket.on("readAllMessagesResponse",handleReadAllMessagesResponse)

      return () => {
        socket.off('typingResponse');
        socket.off("roomResponse", onRoomResponse);
        socket.off("readAllMessagesResponse",handleReadAllMessagesResponse)
      };
    }
  }, [socket, roomId,receiverId]);

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
    autosize(); // Trigger height adjustment on every input change
    handleTyping(senderId, chat?.participantId);
  };
  
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && e.shiftKey) {
      // Allow newline when Shift + Enter is pressed
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault(); // Prevent default Enter behavior
      handleSendMessage(); // Send the message
      setInputValue(''); // Clear the input
    }
  };

const MAX_IMAGES = 20;
const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/jpg'];

const handleFileUpload = async (e) => {
  setFileUploading(true);

  try {
    const selectedFiles = Array.from(e.target.files || []);

    const invalidFiles = selectedFiles.filter(
      file => !ALLOWED_IMAGE_TYPES.includes(file.type)
    );

    if (invalidFiles.length) {
      alert('Only image files (JPG, PNG, WEBP) are allowed.');
      return;
    }

    if (files.length + selectedFiles.length > MAX_IMAGES) {
      alert(`You can upload up to ${MAX_IMAGES} images only.`);
      return;
    }

    const uploadedFiles = await Promise.all(
      selectedFiles.map(file => fileUpload(file, senderId))
    );

    setFiles(prev => [...prev, ...uploadedFiles]);
    setRecordedAudio(null);

  } catch (error) {
    console.error('File upload error:', error);
  } finally {
    setFileUploading(false);
    e.target.value = '';
  }
};


  const handleAudioUpload = async (audioFileBuffer) => {
    if (!audioFileBuffer) return;
    setFileUploading(true);
    try {
      const resFileData = await fileUpload(audioFileBuffer, senderId);
      setFiles((prevArray) => [...prevArray, resFileData]);
      setAudioFile(null);
      console.log('Audio file uploaded successfully');
    } catch (error) {
      console.log('Error uploading audio file:', error);
    }finally{
      setFileUploading(false);
    }
  };

  const handleRemoveFile = (index) => {
    // setUploadedFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
    setFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
  };

  const onEmojiClick = (emojiObject) => {
    setInputValue((prevValue) => prevValue + emojiObject.emoji);
  };

  const toggleProfileCard = () => {
    setShowProfileCard((prev) => !prev); // Toggle the visibility of ProfileCardRight
  };

  const closeProfileCard = () => {
    setShowProfileCard(false); // Close the ProfileCardRight component
  };

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };


  const startRecording = async () => {
    try {
      const permissionStatus = await navigator.permissions.query({ name: 'microphone' });
      if (permissionStatus.state === 'denied') {
        alert('Microphone access is denied. Please enable it in your browser settings.');
        return;
      }
    } catch (err) {
      console.log('err', err)
    }
    navigator.mediaDevices.getUserMedia({ audio: true })
      .then((stream) => {
        audioStreamRef.current = stream;
        mediaRecorderRef.current = new MediaRecorder(stream);
        mediaRecorderRef.current.start();
        setIsRecording(true);
        setRecordingSeconds(0);

        // Start timer
        timerRef.current = setInterval(() => {
          setRecordingSeconds((prev) => prev + 1);
        }, 1000);

        const audioChunks = [];
        mediaRecorderRef.current.ondataavailable = (event) => {
          audioChunks.push(event.data);
        };

        mediaRecorderRef.current.onstop = async () => {
          const audioBlob = new Blob(audioChunks, { type: 'audio/wav' });
          setRecordedAudio({
            blob: audioBlob,
            url: URL.createObjectURL(audioBlob),
            duration: recordingSeconds
          });

          const audioFile = new File([audioBlob], 'audio_recording.wav', { type: 'audio/wav' });
          setAudioFile(audioFile);
          await handleAudioUpload(audioFile);
        };
      })
      .catch((err) => {
        console.error('Error accessing microphone: ', err);
      });
  };

  const stopRecording = async () => {
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
    }
    if (audioStreamRef.current) {
      audioStreamRef.current.getTracks().forEach(track => track.stop());
    }
    clearInterval(timerRef.current);
    setIsRecording(false);

    await handleAudioUpload()
    console.log('audio successfully added')
  };


  const handleClearAudio =()=>{
     setRecordedAudio(null)
     setFiles(files?.filter(ele=>ele?.fileType!=="audio/wav"))
  }


useEffect(() => {
    function handleClickOutside(event) {
      if (emojiPickerRef.current && !emojiPickerRef.current.contains(event.target)) {
        setShowEmojiPicker(false);
      }
    }
    if (showEmojiPicker) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showEmojiPicker]);
  

  return (
    <div className="chatbox d-flex">
      <div className="modal-dialog-scrollable w-100">
        <div className="modal-content" style={{ position: 'relative' }}>
          {chat?.participantId?.isInvisible && <DisabledAccountOverlay />}
          {/* Chatbox Header */}
          <div className="msg-head headChatUser">
            <div className="row">
              <div className="col-8">
                <div className="d-flex align-items-center">
                  <span className="chat-icon">
                    <img
                      className="img-fluid"
                      src="https://mehedihtml.com/chatbox/assets/img/arroleftt.svg"
                      alt="arrow"
                    />
                  </span>
                  <div className="flex-shrink-0 userImgActive">
                    <img
                      className="img-fluid"
                      // src={chat?.participantId?.profilePicture}
                      src={chat?.participantId?.profilePicture || getAvatarByGender(chat?.participantId?.gender)}
                      alt={chat.alt}
                    />
                  </div>
                  <div className="flex-grow-1 ms-3 userNameActive">
                    <h3>{chat?.participantId?.firstName ?? chat?.participantId?.userName}
                      <Typography variant='span' sx={{fontSize:'11px', fontWeight:'500', display:'block'}}>
                        {chat?.participantId?.gender}{','} {chat?.participantId?.city}{' - '}
                      {chat?.participantId?.age}</Typography>
                      {/* {chat?.participantId?.country} */}
                    </h3>
                    <p>{isTyping ? 'Typing...' : ''}</p>
                    {/* <p>{chat.role}</p> */}
                  </div>
                </div>
              </div>
              <div className="col-4">
                <div className="d-flex justify-content-end align-items-center">


                  <Button
                    sx={{
                      minWidth: '40px',
                      maxWidth: '40px',
                      padding: '5px',
                      textTransform: 'capitalize',
                    }}
                    onClick={toggleProfileCard}
                  >
                    {' '}
                    {/* Show ProfileCardRight on click */}
                    <img
                      src={
                        showProfileCard
                          ? '/assets/images/messages/user-active.svg' // Active state image
                          : '/assets/images/messages/user.svg' // Default state image
                      }
                      className="img-fluid"
                      alt="user"
                    />
                  </Button>
                </div>
              </div>
              {/* Dropdown */}
              {isOpen && (
                <div className="position-absolute right-0 inputUsertextSearch  shadow-lg p-4 z-3 bg-white">
                  <input
                    type="text"
                    placeholder="Search..."
                    className="w-100 border border-gray-300 rounded-3 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              )}
            </div>
          </div>
          {/* Chatbox Messages */}
          <div className="modal-body bg-EB overflowauto">
            <div className="msg-body">
              <ul className="p-44">
              
                {/* The container clears `messages` on every chat switch, so without this
                    branch the pane would flash an empty thread while the new room loads. */}
                {messagesLoading && (
                  <li className="text-center text-muted py-4" style={{ listStyle: 'none' }}>
                    Loading messages...
                  </li>
                )}

                {!messagesLoading && messages.map((msg, index) => {
                  return <li
                    key={index}
                    className={msg.senderId !== senderId ? 'sender' : 'repaly'}
                  >
                    <span className="time">
                      {msg?.schedule
                        ? formatMessageTimestamp(msg?.scheduledDateTime)
                        : msg?.timestamp
                          ? formatMessageTimestamp(msg?.timestamp)
                          : ""
                      }
                    </span>
                    <MessageContent msg={msg}  userId={senderId}/>
                 
                    {msg.senderId == senderId  && (
                      <div className="view-unview">
                        {msg.schedule ? (
                          <AccessTimeIcon style={{ color: "orange" }} />
                        ) : (
                          msg.isDelivered ? (
                            <DoneAllIcon style={{ color: msg.isRead ? "blue" : "gray" }} />
                          ) : (
                            <DoneIcon />
                          )
                        )}

                      </div>
                    )}
                  </li>
                })}



                <div ref={messagesEndRef} />
              </ul>
            </div>
          </div>
          {/* Message Input Section */}
          <div className="send-box">
            <div className="attachfile">
              {files.map((file, index) => {
                // console.log('file', file)
                if (file.fileType === 'audio/wav') {
                  return <div className="uploadImg" key={index}>
                    <img src="/assets/images/messages/audio-waves.svg"
                      className="img-fluid" alt={file.name} />
                    <CancelIcon
                      className="icon"
                      onClick={() => {handleRemoveFile(index);setRecordedAudio(null)}}
                    />
                  </div>
                }
                return <div className="uploadImg" key={index}>
                  <img src={file.url} alt={file.name} />
                  <CancelIcon
                    className="icon"
                    onClick={() => handleRemoveFile(index)}
                  />
                </div>
              })}
              {fileUploading && <div className="d-flex align-items-center justify-content-center">
                <div className="spinner-border text-primary mb-3" role="status">
                  {/* <span className="sr-only">Loading...</span> */}
                </div>
              </div>}
            </div>

            <div className="sexBoxSetup d-flex justify-content-between align-items-center" style={{ position: 'relative', zIndex: 2, }}>
              <div className="emoji-picker-container emoji-pickerbg" ref={emojiPickerRef} style={{ position: 'absolute', bottom: '100%', left: '15px', right: '15px', }}>
                {showEmojiPicker && (
                  <div className="emoji-picker">
                    <EmojiPicker
                      onEmojiClick={onEmojiClick}
                    />
                  </div>
                )}
              </div>
              {recordedAudio && !isRecording ? (
                <div className='push1plybnt w-100'>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
                    <div className='d-flex align-items-center gap-4'>
                      <Button style={{ minWidth: '50px', maxWidth: '50px', marginLeft: '0' }}
                        onClick={isPlaying ? pauseRecordedAudio : playRecordedAudio}
                        data-tooltip-id="play-pause-tooltip"
                        data-tooltip-content={isPlaying ? "Pause recording" : "Play recording"}
                      >
                        <img
                          src={isPlaying ? "/assets/images/messages/push1.svg" : "/assets/images/messages/play.svg"}
                          className="img-fluid" style={{ minWidth: '37px' }}
                          alt={isPlaying ? "Pause recording" : "Play recording"}
                        />
                      </Button>
                      <Tooltip
                        id="play-pause-tooltip"
                        place="top"
                        style={{
                          backgroundColor: isPlaying ? "#d32f2f" : "#2e7d32", // Red for pause, green for play
                          color: "white",
                          borderRadius: "4px",
                          padding: "4px 8px",
                          fontSize: "12px",
                          zIndex: 9999
                        }}
                      />
                      <span style={{ fontSize: '14px', display: 'flex', whiteSpace: 'nowrap' }}>
                        {formatTime(currentPlayTime)} / {formatTime(audioDuration)}
                      </span>
                    </div>
                    <Button data-tooltip-id="clear-audio-tooltip" style={{ minWidth: '50px', maxWidth: '50px' }} onClick={handleClearAudio}>
                      <ClearIcon sx={{ color: '#c52424' }} />
                    </Button>
                    <Tooltip
                      id="clear-audio-tooltip"
                      content="Delete recording"  // Tooltip text
                      place="top"               // Position (top, bottom, left, right)
                      style={{
                        backgroundColor: "#c52424", // Matching the icon color
                        color: "white",
                        borderRadius: "4px",
                        padding: "4px 8px",
                        fontSize: "12px",
                        zIndex: 9999
                      }}
                    />
                  </div>
                </div>
              ) : isRecording ? (
                <div className='push1plybnt w-100'>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Button style={{ minWidth: '50px', maxWidth: '50px', marginLeft: '0' }}>
                      <img src="/assets/images/messages/voice-active.svg" className="img-fluid recording-indicator" style={{ minWidth: '25px' }} alt="Stop recording" />
                    </Button>
                    <span style={{ fontSize: '14px', color: '#ff4c4c' }}>{formatTime(recordingSeconds)}</span>
                  </div>
                </div>
              ) : (
                <>
                  <div className="emoji-icon">
                    <button className='border-0 bg-none p-0 text-black' onClick={() => setShowEmojiPicker((prev) => !prev)}>
                      {!showEmojiPicker ? <SentimentSatisfiedOutlinedIcon /> : <CloseOutlinedIcon sx={{ color: '#fff', fontSize: '12px', width: '30px', height: '30px', borderRadius: '50px', background: 'rgba(201, 30, 30, 1)' }} />}
                    </button>
                  </div>
                  <TextareaAutosize className='placeholderFont'
                    placeholder="Type a message..."
                    value={inputValue}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyPress}
                    ref={inputRef}
                    minRows={1}
                    maxRows={3}
                    style={{ fontSize: "16px", resize: "none", border: "none", paddingLeft: '7px', paddingRight: '10px', maxHeight: "50px", outline: "none", width: "100%", }}
                  />
                </>
              )}

              <input
                type="file"
                multiple
                accept="image/*"
                style={{ display: 'none' }}
                id="file-upload-image"
                onChange={(event) => {
                  handleFileUpload(event);
                }}
              />
              <div className='d-flex align-items-center gap-1 dgapstu1'>

                <div className="audio-record-btns" style={{ display: inputValue?.trim() ? "none" : "flex" }}>
                  {recordedAudio && (
                    <audio
                      ref={audioRef}
                      src={recordedAudio.url}
                      onLoadedMetadata={(e) => setAudioDuration(e.currentTarget.duration)}
                      onTimeUpdate={(e) => setCurrentPlayTime(e.currentTarget.currentTime)}
                      onEnded={handleAudioEnded}
                      onPause={() => setIsPlaying(false)}
                      onPlay={() => setIsPlaying(true)}
                    />
                  )}

                  {(!isRecording) ? (
                    !recordedAudio ? (
                      <Button data-tooltip-id="start-recording-tooltip" sx={{ minWidth: '30px', maxWidth: '30px' }} disabled={files?.length > 0 || fileUploading} onClick={startRecording}>
                        <img src="/assets/images/messages/voice.svg" className="img-fluid" alt="Start recording" />
                      </Button>
                    ) : null 
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Button sx={{ minWidth: '30px', maxWidth: '30px', padding: '0' }} data-tooltip-id="stop-recording-tooltip" onClick={stopRecording}>
                        <img src="/assets/images/messages/voice-active1.svg" className="img-fluid" alt="Stop recording" />
                      </Button>
                    </div>
                  )}
                </div>
              <Button
                sx={{
                  minWidth: '30px',
                  maxWidth: '30px',
                  background: 'none',
                  padding: '5px 3px',
                }}
                disabled={fileUploading || isAudioFile}
                onClick={() => !isAudioFile && document.getElementById('file-upload-image').click()}
              >
                <img
                  src="/assets/images/messages/attachfile.svg"
                  className="img-fluid"
                  alt=""
                />
              </Button>
             
                <Button
                  sx={{
                    minWidth: '30px',
                    maxWidth: '30px',
                    background: 'none',
                    padding: '5px 3px', 
                  }}
                  data-tooltip-id="schedule-modal" 
                  onClick={() => setOpenScheduleModal(true)}
                ><AlarmAddIcon sx={{ fill: '#6b6b6b' }} />
                </Button>
              <Tooltip content="Schedule Message" id='schedule-modal' place="top"
                  style={{
                    backgroundColor: "#333",
                    color: "white",
                    borderRadius: "4px",
                    padding: "4px 8px",
                    fontSize: "12px",
                    zIndex: 9999
                  }}></Tooltip>
                 <Button
                sx={{
                  textTransform: 'capitalize',
                  background: '#916008',
                  padding: '0.375rem 20px',
                  fontSize:'14px'
                }}
                type="button"
                onClick={() => {
                  handleSendMessage();
                  setShowEmojiPicker(false);
                }}
              >
                Send
              </Button>

                <Tooltip
                  id="start-recording-tooltip"
                  content="Start voice recording"
                  place="top"
                  style={{
                    backgroundColor: "#333",
                    color: "white",
                    borderRadius: "4px",
                    padding: "4px 8px",
                    fontSize: "12px",
                    zIndex: 9999
                  }}
                />

                <Tooltip
                  id="stop-recording-tooltip"
                  content="Stop recording"
                  place="top"
                  style={{
                    backgroundColor: "#d32f2f", // Red color for stop action
                    color: "white",
                    borderRadius: "4px",
                    padding: "4px 8px",
                    fontSize: "12px",
                    zIndex: 9999
                  }}
                />

                <Tooltip
                  id="attach-file-tooltip"
                  content="Attach file"
                  place="top"
                  style={{
                    backgroundColor: "#333",
                    color: "white",
                    borderRadius: "4px",
                    padding: "4px 8px",
                    fontSize: "12px",
                    zIndex: 9999
                  }}
                />
              </div>
            </div>
          </div>


        </div>
      </div>

      {showProfileCard && (
        <div className="profile-cardRight">
          <ProfileCardRight
            closeProfileCard={closeProfileCard}
            userId={chat?.participantId?._id}
            senderId={senderId}
          />
        </div>
      )}

      <ScheduleModal
        open={openScheduleModal}
        onClose={() => setOpenScheduleModal(false)}
        onSelect={handleScheduleSelect} />


    </div>
  );
};

export default ChatBox;
